import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { X, Truck, Clock, CheckCircle2, MapPin, Star, DollarSign, Wallet, Check, History } from 'lucide-react'
import { api } from '../lib/api'
import { formatDateTime } from '../lib/utils'

interface PeriodStats {
  orders: number
  earnings_fils: number
}

interface PerformanceData {
  driver: { id: string; full_name: string; phone: string; active: boolean; online: boolean }
  today: PeriodStats
  last_7d: PeriodStats
  last_30d: PeriodStats
  lifetime: PeriodStats
  tips_lifetime_fils: number
  performance: {
    avg_delivery_minutes: number | null
    on_time_rate: number | null
    sla_minutes: number
    sample_size: number
    avg_rating: number | null
    rating_count: number
  }
  recent_orders: Array<{
    id: string
    order_number: string
    delivery_address: string | null
    driver_earnings_fils: number
    out_for_delivery_at: string | null
    delivered_at: string | null
    delivery_minutes: number | null
  }>
}

const formatJOD = (fils: number) => `JD ${(fils / 1000).toFixed(3)}`

export default function AmbassadorPerformanceModal({
  driverId,
  onClose,
}: {
  driverId: string
  onClose: () => void
}) {
  const { data, isLoading } = useQuery<PerformanceData>({
    queryKey: ['ambassador-performance', driverId],
    queryFn: () => api.get(`/drivers/${driverId}/performance`).then(r => r.data),
  })

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 px-4 py-6">
      <div className="bg-[#1a1a1a] border border-[#2e2e2e] rounded-xl w-full max-w-3xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#2e2e2e]">
          <h2 className="text-sm font-semibold text-white">Ambassador performance</h2>
          <button onClick={onClose}><X size={16} className="text-gray-400" /></button>
        </div>
        <div className="overflow-y-auto p-5">
          {isLoading || !data ? (
            <div className="py-12 text-center text-gray-600 text-sm">Loading…</div>
          ) : (
            <>
              <Header data={data} />
              <PeriodGrid data={data} />
              <CashCard driverId={driverId} />
              <PerformanceCard data={data} />
              <RecentOrders data={data} />
            </>
          )}
        </div>
      </div>
    </div>
  )
}

function Header({ data }: { data: PerformanceData }) {
  const { driver } = data
  return (
    <div className="flex items-center gap-3 pb-5 mb-5 border-b border-[#2e2e2e]">
      <div className="w-12 h-12 rounded-full bg-[#2a2a2a] border border-[#3a3a3a] flex items-center justify-center">
        <Truck size={18} className="text-gray-500" />
      </div>
      <div className="flex-1">
        <p className="text-base font-semibold text-white">{driver.full_name}</p>
        <p className="text-xs text-gray-500 font-mono mt-0.5">{driver.phone}</p>
      </div>
      <div className="flex flex-col gap-1.5 items-end">
        <span className={`text-xs px-2 py-0.5 rounded-full ${
          driver.active ? 'bg-green-500/15 text-green-400' : 'bg-gray-500/15 text-gray-500'
        }`}>{driver.active ? 'Active' : 'Inactive'}</span>
        <div className="flex items-center gap-1.5">
          <div className={`w-2 h-2 rounded-full ${driver.online ? 'bg-green-400' : 'bg-gray-600'}`} />
          <span className={`text-xs ${driver.online ? 'text-green-400' : 'text-gray-600'}`}>
            {driver.online ? 'Online' : 'Offline'}
          </span>
        </div>
      </div>
    </div>
  )
}

function PeriodGrid({ data }: { data: PerformanceData }) {
  const cells: Array<{ label: string; stats: PeriodStats; accent?: boolean }> = [
    { label: 'Today',        stats: data.today },
    { label: 'Last 7 days',  stats: data.last_7d },
    { label: 'Last 30 days', stats: data.last_30d },
    { label: 'Lifetime',     stats: data.lifetime, accent: true },
  ]
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
      {cells.map(c => (
        <div key={c.label} className={`rounded-lg p-3 border ${
          c.accent
            ? 'bg-[#C41E3A]/10 border-[#C41E3A]/30'
            : 'bg-white/[0.02] border-[#2e2e2e]'
        }`}>
          <p className="text-xs text-gray-500 mb-1">{c.label}</p>
          <p className={`text-lg font-bold ${c.accent ? 'text-[#C41E3A]' : 'text-white'} tabular-nums`}>
            {formatJOD(c.stats.earnings_fils)}
          </p>
          <p className="text-xs text-gray-500 mt-0.5">
            {c.stats.orders} {c.stats.orders === 1 ? 'order' : 'orders'}
          </p>
        </div>
      ))}
    </div>
  )
}

function PerformanceCard({ data }: { data: PerformanceData }) {
  const { performance: p } = data
  const onTimeColor =
    p.on_time_rate == null ? 'text-gray-500'
    : p.on_time_rate >= 90 ? 'text-green-400'
    : p.on_time_rate >= 75 ? 'text-amber-400'
    :                        'text-[#C41E3A]'

  return (
    <div className="bg-white/[0.02] border border-[#2e2e2e] rounded-lg p-4 mb-5">
      <p className="text-xs text-gray-500 uppercase tracking-widest mb-3">Performance (lifetime)</p>
      {p.sample_size === 0 && p.rating_count === 0 ? (
        <p className="text-sm text-gray-500">No timed deliveries or ratings yet.</p>
      ) : (
        <div className="grid grid-cols-3 gap-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Clock size={14} className="text-gray-500" />
              <p className="text-xs text-gray-500">Avg delivery time</p>
            </div>
            <p className="text-lg font-bold text-white tabular-nums">
              {p.avg_delivery_minutes != null ? `${p.avg_delivery_minutes.toFixed(1)} min` : '—'}
            </p>
          </div>
          <div>
            <div className="flex items-center gap-2 mb-1">
              <CheckCircle2 size={14} className="text-gray-500" />
              <p className="text-xs text-gray-500">
                On-time rate <span className="text-gray-600">(target: {p.sla_minutes}m)</span>
              </p>
            </div>
            <p className={`text-lg font-bold tabular-nums ${onTimeColor}`}>
              {p.on_time_rate != null ? `${p.on_time_rate.toFixed(1)}%` : '—'}
            </p>
          </div>
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Star size={14} className="text-amber-400" />
              <p className="text-xs text-gray-500">Avg rating</p>
            </div>
            {p.avg_rating != null ? (
              <div className="flex items-baseline gap-1.5">
                <p className="text-lg font-bold text-amber-400 tabular-nums">{p.avg_rating.toFixed(1)}</p>
                <p className="text-[11px] text-gray-500">({p.rating_count})</p>
              </div>
            ) : (
              <p className="text-lg font-bold text-gray-500">—</p>
            )}
          </div>
          <p className="col-span-3 text-xs text-gray-600">
            Based on {p.sample_size} timed {p.sample_size === 1 ? 'delivery' : 'deliveries'}
            {p.rating_count > 0 && ` · ${p.rating_count} rating${p.rating_count === 1 ? '' : 's'}`}
          </p>
        </div>
      )}

      {/* Tips earned (lifetime) — show only if non-zero. */}
      {data.tips_lifetime_fils > 0 && (
        <div className="mt-4 pt-4 border-t border-[#2e2e2e] flex items-center gap-2">
          <DollarSign size={14} className="text-green-400" />
          <p className="text-xs text-gray-500">Lifetime tips earned</p>
          <p className="ml-auto text-sm font-bold text-green-400 tabular-nums">
            {formatJOD(data.tips_lifetime_fils)}
          </p>
        </div>
      )}
    </div>
  )
}

function RecentOrders({ data }: { data: PerformanceData }) {
  const orders = data.recent_orders
  return (
    <div>
      <p className="text-xs text-gray-500 uppercase tracking-widest mb-3">
        Recent deliveries ({orders.length})
      </p>
      {orders.length === 0 ? (
        <p className="text-sm text-gray-500 py-4">No deliveries yet.</p>
      ) : (
        <div className="space-y-2">
          {orders.map(o => (
            <div key={o.id} className="bg-white/[0.02] border border-[#222] rounded-lg p-3">
              <div className="flex items-center justify-between mb-1">
                <p className="text-sm font-medium text-white">#{o.order_number}</p>
                <p className="text-sm font-semibold text-white tabular-nums">
                  {formatJOD(o.driver_earnings_fils)}
                </p>
              </div>
              {o.delivery_address && (
                <div className="flex items-start gap-1.5 mb-1">
                  <MapPin size={11} className="text-gray-600 mt-0.5 shrink-0" />
                  <p className="text-xs text-gray-500 line-clamp-1">{o.delivery_address}</p>
                </div>
              )}
              <div className="flex items-center justify-between mt-1.5">
                <p className="text-xs text-gray-600">
                  {o.delivered_at ? formatDateTime(o.delivered_at) : '—'}
                </p>
                {o.delivery_minutes != null && (
                  <p className="text-xs text-gray-500 tabular-nums">
                    {o.delivery_minutes.toFixed(1)} min
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ────────────────────────────────────────────────────────────────────────────
// Cash reconciliation card + settle modal + history view
// ────────────────────────────────────────────────────────────────────────────

interface PendingCash {
  pending_amount_fils: number
  pending_orders_count: number
  orders: Array<{
    id: string; order_number: string;
    total_fils: number; tip_fils: number; due_fils: number;
    delivered_at: string;
  }>
}

interface SettlementHistoryRow {
  id: string
  amount_fils: number
  orders_count: number
  expected_fils: number
  notes: string | null
  created_at: string
  settled_by_name: string | null
}

function CashCard({ driverId }: { driverId: string }) {
  const [settling, setSettling] = useState(false)
  const [showHistory, setShowHistory] = useState(false)

  const { data, isLoading } = useQuery<PendingCash>({
    queryKey: ['ambassador-cash', driverId],
    queryFn: () => api.get(`/drivers/${driverId}/cash`).then(r => r.data),
  })

  const isZero = !data || data.pending_orders_count === 0

  return (
    <>
      <div className={`rounded-lg p-4 mb-5 border ${
        isZero
          ? 'bg-white/[0.02] border-[#2e2e2e]'
          : 'bg-amber-500/10 border-amber-500/30'
      }`}>
        <div className="flex items-start justify-between gap-3 mb-2">
          <div className="flex items-center gap-2">
            <Wallet size={14} className={isZero ? 'text-gray-500' : 'text-amber-400'} />
            <p className="text-xs text-gray-500 uppercase tracking-widest">Cash to settle</p>
          </div>
          <button
            onClick={() => setShowHistory(true)}
            className="flex items-center gap-1 text-[11px] text-gray-500 hover:text-white transition-colors"
          >
            <History size={11} /> History
          </button>
        </div>

        {isLoading ? (
          <p className="text-sm text-gray-600 py-2">Loading…</p>
        ) : isZero ? (
          <p className="text-sm text-gray-500">All settled. Nothing pending.</p>
        ) : (
          <>
            <div className="flex items-baseline gap-3">
              <p className="text-2xl font-bold text-amber-400 tabular-nums">{formatJOD(data!.pending_amount_fils)}</p>
              <p className="text-xs text-gray-500">
                {data!.pending_orders_count} {data!.pending_orders_count === 1 ? 'order' : 'orders'} pending
              </p>
            </div>
            <button
              onClick={() => setSettling(true)}
              className="mt-3 flex items-center gap-1.5 bg-amber-500/20 hover:bg-amber-500/30 text-amber-400 text-xs font-medium px-3 py-1.5 rounded-lg transition-colors"
            >
              <Check size={12} /> Settle cash
            </button>
          </>
        )}
      </div>

      {settling && data && (
        <SettleModal
          driverId={driverId}
          pending={data}
          onClose={() => setSettling(false)}
        />
      )}
      {showHistory && (
        <HistoryModal driverId={driverId} onClose={() => setShowHistory(false)} />
      )}
    </>
  )
}

function SettleModal({
  driverId, pending, onClose,
}: {
  driverId: string; pending: PendingCash; onClose: () => void
}) {
  const qc = useQueryClient()
  // Default to the expected amount — admin overrides if cash counted differs.
  const [amountStr, setAmountStr] = useState((pending.pending_amount_fils / 1000).toFixed(3))
  const [notes, setNotes] = useState('')
  const [error, setError] = useState<string | null>(null)

  const mutation = useMutation({
    mutationFn: () => api.post(`/drivers/${driverId}/cash/settle`, {
      amount_fils: Math.max(0, Math.round(parseFloat(amountStr || '0') * 1000)),
      notes: notes.trim() || undefined,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ambassador-cash', driverId] })
      qc.invalidateQueries({ queryKey: ['ambassador-cash-history', driverId] })
      onClose()
    },
    onError: (e: { response?: { data?: { message?: string } } }) =>
      setError(e.response?.data?.message ?? 'Failed to settle'),
  })

  const enteredFils = Math.max(0, Math.round(parseFloat(amountStr || '0') * 1000))
  const variance = enteredFils - pending.pending_amount_fils

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[60] px-4 py-6">
      <div className="bg-[#1a1a1a] border border-[#2e2e2e] rounded-xl w-full max-w-lg max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#2e2e2e]">
          <h2 className="text-sm font-semibold text-white">Settle cash</h2>
          <button onClick={onClose}><X size={16} className="text-gray-400" /></button>
        </div>
        <div className="overflow-y-auto p-5 space-y-4">
          {/* Order list summary */}
          <div className="bg-white/[0.02] border border-[#222] rounded-lg p-3">
            <p className="text-[10px] text-gray-600 uppercase tracking-widest mb-2">
              Covers {pending.pending_orders_count} {pending.pending_orders_count === 1 ? 'order' : 'orders'}
            </p>
            <div className="space-y-1 max-h-40 overflow-y-auto">
              {pending.orders.map(o => (
                <div key={o.id} className="flex items-center justify-between text-xs">
                  <span className="text-gray-400">#{o.order_number}</span>
                  <span className="text-gray-300 tabular-nums">{formatJOD(o.due_fils)}</span>
                </div>
              ))}
            </div>
            <div className="border-t border-[#2e2e2e] mt-2 pt-2 flex items-center justify-between text-sm">
              <span className="text-gray-400">Expected</span>
              <span className="text-white font-semibold tabular-nums">{formatJOD(pending.pending_amount_fils)}</span>
            </div>
          </div>

          <div>
            <label className="block text-xs text-gray-400 mb-1.5">Amount received (JD)</label>
            <input
              type="number" step="0.001" min="0"
              value={amountStr}
              onChange={e => setAmountStr(e.target.value)}
              className="w-full bg-[#242424] border border-[#3a3a3a] rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#C41E3A]"
            />
            {Math.abs(variance) > 0 && (
              <p className={`text-xs mt-1.5 ${variance < 0 ? 'text-[#C41E3A]' : 'text-amber-400'}`}>
                {variance < 0 ? 'Shortfall' : 'Overage'}: {formatJOD(Math.abs(variance))}
              </p>
            )}
          </div>

          <div>
            <label className="block text-xs text-gray-400 mb-1.5">Notes (optional)</label>
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder="Anything to remember about this handoff?"
              rows={2}
              maxLength={1000}
              className="w-full bg-[#242424] border border-[#3a3a3a] rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#C41E3A]"
            />
          </div>

          {error && <p className="text-xs text-red-400">{error}</p>}

          <div className="flex justify-end gap-3 pt-2">
            <button onClick={onClose} className="px-4 py-2 text-sm text-gray-400 hover:text-white">Cancel</button>
            <button
              onClick={() => mutation.mutate()}
              disabled={mutation.isPending}
              className="flex items-center gap-1.5 px-4 py-2 bg-amber-500/20 hover:bg-amber-500/30 text-amber-400 text-sm font-medium rounded-lg disabled:opacity-50"
            >
              <Check size={14} />
              {mutation.isPending ? 'Settling…' : 'Confirm settlement'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

function HistoryModal({ driverId, onClose }: { driverId: string; onClose: () => void }) {
  const { data, isLoading } = useQuery<{ settlements: SettlementHistoryRow[] }>({
    queryKey: ['ambassador-cash-history', driverId],
    queryFn: () => api.get(`/drivers/${driverId}/cash/history`).then(r => r.data),
  })

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[60] px-4 py-6">
      <div className="bg-[#1a1a1a] border border-[#2e2e2e] rounded-xl w-full max-w-lg max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#2e2e2e]">
          <h2 className="text-sm font-semibold text-white">Settlement history</h2>
          <button onClick={onClose}><X size={16} className="text-gray-400" /></button>
        </div>
        <div className="overflow-y-auto p-5">
          {isLoading ? (
            <p className="text-sm text-gray-600 py-6 text-center">Loading…</p>
          ) : !data?.settlements.length ? (
            <p className="text-sm text-gray-500 py-6 text-center">No settlements yet.</p>
          ) : (
            <div className="space-y-3">
              {data.settlements.map(s => {
                const variance = s.amount_fils - s.expected_fils
                return (
                  <div key={s.id} className="bg-white/[0.02] border border-[#222] rounded-lg p-3">
                    <div className="flex items-baseline justify-between mb-1">
                      <p className="text-sm font-semibold text-white tabular-nums">{formatJOD(s.amount_fils)}</p>
                      <p className="text-xs text-gray-500">{formatDateTime(s.created_at)}</p>
                    </div>
                    <p className="text-xs text-gray-500">
                      {s.orders_count} {s.orders_count === 1 ? 'order' : 'orders'}
                      {variance !== 0 && (
                        <span className={`ml-2 ${variance < 0 ? 'text-[#C41E3A]' : 'text-amber-400'}`}>
                          ({variance < 0 ? '−' : '+'}{formatJOD(Math.abs(variance))})
                        </span>
                      )}
                      {s.settled_by_name && <span className="ml-2 text-gray-600">· by {s.settled_by_name}</span>}
                    </p>
                    {s.notes && (
                      <p className="text-xs text-gray-400 mt-2 whitespace-pre-wrap">{s.notes}</p>
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
